import authService from './authService';
import {
  Acquirer,
  AcquirerFormData,
  AcquirerSearchModel,
  AcquirerCreditCardConfiguration,
  AcquirerCreditCardConfigurationFormData,
  AcquirerCreditCardConfigurationSearchModel,
  AcquirerPIXConfiguration,
  AcquirerPIXConfigurationFormData,
  AcquirerPIXConfigurationSearchModel,
  PIXCertificateResult,
  PIXCertificateLogEntry,
  AcquirerCardTax,
  AcquirerCardTaxFormData,
  AcquirerCardTaxSearchModel,
  AcquirerPIXTax,
  AcquirerPIXTaxFormData,
  AcquirerPIXTaxSearchModel,
} from '../types/acquirer';
import { PaginatedResponse, ApiResponse } from '../types/common';

class AcquirerService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ===== Acquirer =====

  async getAcquirers(searchModel: AcquirerSearchModel): Promise<PaginatedResponse<Acquirer>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirers?${params}`);

    if (!response.ok) throw new Error('Falha ao buscar adquirentes');

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async listAllAcquirers(): Promise<Acquirer[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/financial/acquirers?page=1&page_size=100');
    if (!response.ok) throw new Error('Falha ao buscar adquirentes');
    const json = await response.json();
    return Array.isArray(json.data) ? json.data : [];
  }

  async getAcquirerById(id: number): Promise<Acquirer> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirers/${id}`);
    if (!response.ok) throw new Error('Falha ao buscar adquirente');
    const data: ApiResponse<Acquirer> = await response.json();
    return data.data!;
  }

  async createAcquirer(formData: AcquirerFormData): Promise<Acquirer> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/acquirers', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar adquirente');
    }
    const data: ApiResponse<Acquirer> = await response.json();
    return data.data!;
  }

  async updateAcquirer(id: number, formData: AcquirerFormData): Promise<Acquirer> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/acquirers/${id}`, formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar adquirente');
    }
    const data: ApiResponse<Acquirer> = await response.json();
    return data.data!;
  }

  async deleteAcquirer(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/acquirers/${id}`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir adquirente');
    }
  }

  // ===== AcquirerCreditCardConfiguration =====

  async getCreditCardConfigs(searchModel: AcquirerCreditCardConfigurationSearchModel): Promise<PaginatedResponse<AcquirerCreditCardConfiguration>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.acquirer_id && { acquirer_id: searchModel.acquirer_id.toString() }),
      ...(searchModel.company_id && { company_id: searchModel.company_id.toString() }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirer-credit-card-configurations?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar configurações de cartão de crédito');

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getCreditCardConfigById(id: number): Promise<AcquirerCreditCardConfiguration> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirer-credit-card-configurations/${id}`);
    if (!response.ok) throw new Error('Falha ao buscar configuração de cartão');
    const data: ApiResponse<AcquirerCreditCardConfiguration> = await response.json();
    return data.data!;
  }

  async createCreditCardConfig(formData: AcquirerCreditCardConfigurationFormData): Promise<AcquirerCreditCardConfiguration> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/acquirer-credit-card-configurations', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar configuração de cartão');
    }
    const data: ApiResponse<AcquirerCreditCardConfiguration> = await response.json();
    return data.data!;
  }

  async updateCreditCardConfig(id: number, formData: AcquirerCreditCardConfigurationFormData): Promise<AcquirerCreditCardConfiguration> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/acquirer-credit-card-configurations/${id}`, formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar configuração de cartão');
    }
    const data: ApiResponse<AcquirerCreditCardConfiguration> = await response.json();
    return data.data!;
  }

  async deleteCreditCardConfig(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/acquirer-credit-card-configurations/${id}`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir configuração de cartão');
    }
  }

  // ===== AcquirerPIXConfiguration =====

  async getPIXConfigs(searchModel: AcquirerPIXConfigurationSearchModel): Promise<PaginatedResponse<AcquirerPIXConfiguration>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.acquirer_id && { acquirer_id: searchModel.acquirer_id.toString() }),
      ...(searchModel.company_id && { company_id: searchModel.company_id.toString() }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirer-pix-configurations?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar configurações PIX');

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getPIXConfigById(id: number): Promise<AcquirerPIXConfiguration> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirer-pix-configurations/${id}`);
    if (!response.ok) throw new Error('Falha ao buscar configuração PIX');
    const data: ApiResponse<AcquirerPIXConfiguration> = await response.json();
    return data.data!;
  }

  async createPIXConfig(formData: AcquirerPIXConfigurationFormData): Promise<AcquirerPIXConfiguration> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/acquirer-pix-configurations', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar configuração PIX');
    }
    const data: ApiResponse<AcquirerPIXConfiguration> = await response.json();
    return data.data!;
  }

  async updatePIXConfig(id: number, formData: AcquirerPIXConfigurationFormData): Promise<AcquirerPIXConfiguration> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/acquirer-pix-configurations/${id}`, formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar configuração PIX');
    }
    const data: ApiResponse<AcquirerPIXConfiguration> = await response.json();
    return data.data!;
  }

  async deletePIXConfig(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/acquirer-pix-configurations/${id}`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir configuração PIX');
    }
  }

  // ===== AcquirerCardTax =====

  async getCardTaxes(searchModel: AcquirerCardTaxSearchModel): Promise<PaginatedResponse<AcquirerCardTax>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.acquirer_id && { acquirer_id: searchModel.acquirer_id.toString() }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirer-card-taxes?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar taxas de cartão');

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getCardTaxById(id: number): Promise<AcquirerCardTax> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirer-card-taxes/${id}`);
    if (!response.ok) throw new Error('Falha ao buscar taxa de cartão');
    const data: ApiResponse<AcquirerCardTax> = await response.json();
    return data.data!;
  }

  async createCardTax(formData: AcquirerCardTaxFormData): Promise<AcquirerCardTax> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/acquirer-card-taxes', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar taxa de cartão');
    }
    const data: ApiResponse<AcquirerCardTax> = await response.json();
    return data.data!;
  }

  async updateCardTax(id: number, formData: AcquirerCardTaxFormData): Promise<AcquirerCardTax> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/acquirer-card-taxes/${id}`, formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar taxa de cartão');
    }
    const data: ApiResponse<AcquirerCardTax> = await response.json();
    return data.data!;
  }

  async deleteCardTax(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/acquirer-card-taxes/${id}`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir taxa de cartão');
    }
  }

  // ===== AcquirerPIXTax =====

  async getPIXTaxes(searchModel: AcquirerPIXTaxSearchModel): Promise<PaginatedResponse<AcquirerPIXTax>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.acquirer_id && { acquirer_id: searchModel.acquirer_id.toString() }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirer-pix-taxes?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar taxas PIX');

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getPIXTaxById(id: number): Promise<AcquirerPIXTax> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/acquirer-pix-taxes/${id}`);
    if (!response.ok) throw new Error('Falha ao buscar taxa PIX');
    const data: ApiResponse<AcquirerPIXTax> = await response.json();
    return data.data!;
  }

  async createPIXTax(formData: AcquirerPIXTaxFormData): Promise<AcquirerPIXTax> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/acquirer-pix-taxes', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar taxa PIX');
    }
    const data: ApiResponse<AcquirerPIXTax> = await response.json();
    return data.data!;
  }

  async updatePIXTax(id: number, formData: AcquirerPIXTaxFormData): Promise<AcquirerPIXTax> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/acquirer-pix-taxes/${id}`, formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar taxa PIX');
    }
    const data: ApiResponse<AcquirerPIXTax> = await response.json();
    return data.data!;
  }

  async deletePIXTax(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/acquirer-pix-taxes/${id}`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir taxa PIX');
    }
  }

  // ===== Certificado mTLS do PIX =====

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  private async throwApiError(response: Response, fallback: string): Promise<never> {
    let message = fallback;
    try {
      const body = await response.json();
      message = body?.message || body?.error || fallback;
    } catch {
      /* corpo não-JSON — mantém o fallback */
    }
    throw new Error(message);
  }

  /**
   * POST .../certificate — multipart com o arquivo e a senha.
   *
   * Vai por fetch direto, e não pelo apiClient, porque o apiClient força
   * Content-Type: application/json; num FormData o browser precisa definir o
   * Content-Type sozinho para incluir o boundary. Mesmo padrão de
   * bankIntegrationService.uploadCertificate.
   */
  async uploadPIXCertificate(id: number, file: File, password: string): Promise<PIXCertificateResult> {
    const baseUrl = await this.getBaseUrl();
    const fd = new FormData();
    fd.append('file', file);
    fd.append('password', password);

    const url = `${baseUrl}/financial/acquirer-pix-configurations/${id}/certificate`;
    const doFetch = async (token?: string | null) =>
      fetch(url, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) await this.throwApiError(response, 'Falha ao enviar o certificado');
    const data: ApiResponse<PIXCertificateResult> = await response.json();
    return data.data!;
  }

  /**
   * POST .../certificate/test — confere o certificado gravado no Itaú e, por
   * padrão, também o que o PaymentHub tem carregado em memória.
   *
   * Responde 200 mesmo quando o certificado não funciona: o diagnóstico É o
   * resultado. Só erro de requisição (sem permissão, configuração inexistente)
   * chega aqui como exceção.
   */
  async testPIXCertificate(id: number, checkHub = true): Promise<PIXCertificateResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/financial/acquirer-pix-configurations/${id}/certificate/test?check_hub=${checkHub}`,
      {},
    );
    if (!response.ok) await this.throwApiError(response, 'Falha ao testar o certificado');
    const data: ApiResponse<PIXCertificateResult> = await response.json();
    return data.data!;
  }

  /** GET .../certificate/logs — trilha das operações de certificado. */
  async getPIXCertificateLogs(id: number, limit = 200): Promise<PIXCertificateLogEntry[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/financial/acquirer-pix-configurations/${id}/certificate/logs?limit=${limit}`,
    );
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar o histórico do certificado');
    const data: ApiResponse<PIXCertificateLogEntry[]> = await response.json();
    return data.data ?? [];
  }

  /** POST .../certificate/renew — renova junto ao Itaú e coloca em uso. */
  async renewPIXCertificate(id: number): Promise<PIXCertificateResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/financial/acquirer-pix-configurations/${id}/certificate/renew`,
      {},
    );
    if (!response.ok) await this.throwApiError(response, 'Falha ao renovar o certificado');
    const data: ApiResponse<PIXCertificateResult> = await response.json();
    return data.data!;
  }
}

export const acquirerService = new AcquirerService();
export default acquirerService;
